import { VsfContext } from '~/composables/context';
import { Logger } from '~/helpers/logger';
import {
  Cart,
  CartItemInput,
} from '~/modules/GraphQL/types';
import { CustomQuery, CustomHeaders } from '~/types/core';

/** Params object used to add items to a cart */
export declare type AddProductsToCartInput = {
  cartId: string;
  cartItems: CartItemInput[];
};

export const addItemCommand = {
  execute: async (
    context: VsfContext,
    {
      product,
      quantity,
      currentCart,
      productConfiguration,
      customQuery,
      customHeaders,
    },
  ) => {
    Logger.debug('[Magento]: Add item to cart', {
      product,
      quantity,
      currentCart,
    });

    const apiState = context.$magento.config.state;
    const cartId = apiState.getCartId();

    if (!product) {
      return;
    }
    // eslint-disable-next-line no-underscore-dangle
    switch (product.__typename) {
      case 'SimpleProduct':
        const simpleCartInput: AddProductsToCartInput = {
          cartId,
          cartItems: [
            {
              quantity,
              sku: product.sku,
            },
          ],
        };

        const simpleProduct = await context.$magento.api.addProductsToCart(
          simpleCartInput,
          customQuery as CustomQuery,
          customHeaders as CustomHeaders,
        );

        Logger.debug('[Result]:', { data: simpleProduct.data });

        // user_errors is present at runtime (see addProductsToCart GraphQL query) but missing from the generated AddProductsToCartMutation type
        const simpleProductUserErrors = (simpleProduct.data.addProductsToCart as any).user_errors;
        if (simpleProductUserErrors.length > 0) {
          throw new Error(String(simpleProductUserErrors[0].message));
        }

        // eslint-disable-next-line consistent-return
        return simpleProduct
          .data
          .addProductsToCart
          .cart as unknown as Cart;
      case 'ConfigurableProduct':
        const selectedOptions = Object.values(productConfiguration as object);

        const configurableCartInput: AddProductsToCartInput = {
          cartId,
          cartItems: [
            {
              quantity,
              sku: product.sku,
              selected_options: selectedOptions,
            },
          ],
        };

        const configurableProduct = await context.$magento.api.addProductsToCart(
          configurableCartInput,
          customQuery as CustomQuery,
          customHeaders as CustomHeaders,
        );
        Logger.debug('[Result]:', { data: configurableProduct.data });

        // user_errors is present at runtime (see addProductsToCart GraphQL query) but missing from the generated AddProductsToCartMutation type
        const configurableProductUserErrors = (configurableProduct.data.addProductsToCart as any).user_errors;
        if (configurableProductUserErrors.length > 0) {
          throw new Error(String(configurableProductUserErrors[0].message));
        }

        // eslint-disable-next-line consistent-return
        return configurableProduct.data
          .addProductsToCart
          .cart as unknown as Cart;
      case 'BundleProduct':
        const createEnteredOptions = () =>
        // eslint-disable-next-line implicit-arrow-linebreak
          product.bundle_options.map((bundleOption) => ({
            ...bundleOption,
            value: bundleOption.value.toString(),
          }));

        const bundleCartInput: AddProductsToCartInput = {
          cartId,
          cartItems: [
            {
              quantity,
              sku: product.sku,
              entered_options: createEnteredOptions(),
            },
          ],
        };

        const bundleProduct = await context.$magento.api.addProductsToCart(
          bundleCartInput,
          customQuery as CustomQuery,
          customHeaders as CustomHeaders,
        );

        Logger.debug('[Result]:', { data: bundleProduct });

        // user_errors is present at runtime (see addProductsToCart GraphQL query) but missing from the generated AddProductsToCartMutation type
        const bundleProductUserErrors = (bundleProduct.data.addProductsToCart as any).user_errors;
        if (bundleProductUserErrors.length > 0) {
          throw new Error(String(bundleProductUserErrors[0].message));
        }

        // eslint-disable-next-line consistent-return
        return bundleProduct
          .data
          .addProductsToCart
          .cart as unknown as Cart;
      case 'DownloadableProduct':
        const downloadableCartInput: AddProductsToCartInput = {
          cartId,
          cartItems: [
            {
              quantity,
              sku: product.sku,
            },
          ],
        };

        const downloadableProduct = await context.$magento.api.addProductsToCart(
          downloadableCartInput,
          customQuery as CustomQuery,
          customHeaders as CustomHeaders,
        );

        Logger.debug('[Result DownloadableProduct]:', { data: downloadableProduct });

        // user_errors is present at runtime (see addProductsToCart GraphQL query) but missing from the generated AddProductsToCartMutation type
        const downloadableProductUserErrors = (downloadableProduct.data.addProductsToCart as any).user_errors;
        if (downloadableProductUserErrors.length > 0) {
          throw new Error(String(downloadableProductUserErrors[0].message));
        }

        // eslint-disable-next-line consistent-return
        return downloadableProduct
          .data
          .addProductsToCart
          .cart as unknown as Cart;
      case 'VirtualProduct':
        const virtualCartInput: AddProductsToCartInput = {
          cartId,
          cartItems: [
            {
              quantity,
              sku: product.sku,
              entered_options: productConfiguration ? Object.keys(productConfiguration).map(uid=>{ return {uid,value: productConfiguration[uid]}}) : null
            },
          ],
        };
        const virtualProduct = await context.$magento.api.addProductsToCart(
          virtualCartInput,
          customQuery as CustomQuery,
          customHeaders as CustomHeaders,
        );

        Logger.debug('[Result VirtualProduct]:', { data: virtualProduct });

        // user_errors is present at runtime (see addProductsToCart GraphQL query) but missing from the generated AddProductsToCartMutation type
        const virtualProductUserErrors = (virtualProduct.data.addProductsToCart as any).user_errors;
        if (virtualProductUserErrors.length > 0) {
          throw new Error(String(virtualProductUserErrors[0].message));
        }

        // eslint-disable-next-line consistent-return
        return virtualProduct
          .data
          .addProductsToCart
          .cart as unknown as Cart;
      case 'GroupedProduct':
        const groupedCartInput: AddProductsToCartInput = {
          cartId,
          cartItems: product.items.map((item) => ({
            quantity,
            sku: item.product.sku,
          })),
        };

        const groupedProduct = await context.$magento.api.addProductsToCart(groupedCartInput, customQuery as CustomQuery);

        Logger.debug('[Result GroupedProduct]:', { data: groupedProduct });

        // user_errors is present at runtime (see addProductsToCart GraphQL query) but missing from the generated AddProductsToCartMutation type
        const groupedProductUserErrors = (groupedProduct.data.addProductsToCart as any).user_errors;
        if (groupedProductUserErrors.length > 0) {
          throw new Error(String(groupedProductUserErrors[0].message));
        }

        // eslint-disable-next-line consistent-return
        return groupedProduct
          .data
          .addProductsToCart
          .cart as unknown as Cart;
      default:
        // eslint-disable-next-line no-underscore-dangle
        throw new Error(`Product Type ${product.__typename} not supported in add to cart yet`);
    }
  },
};
